import { Trash2, LucideIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { memo, useCallback } from 'react';
import { TransactionFilters } from '@polybank/shared/types';

type AccentColor =
  | 'blue'
  | 'violet'
  | 'orange'
  | 'emerald'
  | 'pink'
  | 'cyan'
  | 'primary';

const accentStyles: Record<
  AccentColor,
  { bar: string; iconBg: string; iconText: string }
> = {
  blue: {
    bar: 'bg-blue-500',
    iconBg: 'bg-blue-500/10',
    iconText: 'text-blue-500',
  },
  violet: {
    bar: 'bg-violet-500',
    iconBg: 'bg-violet-500/10',
    iconText: 'text-violet-500',
  },
  orange: {
    bar: 'bg-orange-500',
    iconBg: 'bg-orange-500/10',
    iconText: 'text-orange-500',
  },
  emerald: {
    bar: 'bg-emerald-500',
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-500',
  },
  pink: {
    bar: 'bg-pink-500',
    iconBg: 'bg-pink-500/10',
    iconText: 'text-pink-500',
  },
  cyan: {
    bar: 'bg-cyan-500',
    iconBg: 'bg-cyan-500/10',
    iconText: 'text-cyan-500',
  },
  primary: {
    bar: 'bg-primary',
    iconBg: 'bg-primary/10',
    iconText: 'text-primary',
  },
};

interface TransactionFiltersFormItemProps {
  label: string;
  isDirty: boolean;
  onClear: (id: keyof TransactionFilters) => void;
  children: React.ReactNode;
  id: keyof TransactionFilters;
  icon?: LucideIcon;
  accent?: AccentColor;
}

export const TransactionFiltersFormItem = memo(
  function TransactionFiltersFormItem({
    label,
    isDirty,
    onClear: onClearProp,
    children,
    id,
    icon: Icon,
    accent = 'primary',
  }: TransactionFiltersFormItemProps) {
    const onClear = useCallback(() => {
      onClearProp(id);
    }, [onClearProp, id]);

    const styles = accentStyles[accent];

    return (
      <div className='relative rounded-xl bg-muted/30 border border-border/50 overflow-hidden transition-all hover:border-border/80 hover:bg-muted/50'>
        {/* Colored accent bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${styles.bar}`} />

        <div className='p-4 pl-5 space-y-3'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              {Icon && (
                <div className={`p-1.5 rounded-lg ${styles.iconBg}`}>
                  <Icon className={`w-4 h-4 ${styles.iconText}`} />
                </div>
              )}
              <Label className='text-sm font-medium'>{label}</Label>
            </div>
            {isDirty && (
              <button
                onClick={onClear}
                className='inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 text-xs font-medium transition-colors'
              >
                <Trash2 className='w-3 h-3' />
                <span>Clear</span>
              </button>
            )}
          </div>
          {children}
        </div>
      </div>
    );
  }
);
